import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { expensesAPI, staffAPI, equipmentAPI } from '../../services/api';
import { ImageCaptureField } from '../../components/ui';
import { resolveUploadUrl } from '../../utils/uploadUrl';

export default function AddExpense() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const receiptFileRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [equipmentList, setEquipmentList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingExpense, setLoadingExpense] = useState(isEdit);
  const [receiptPreview, setReceiptPreview] = useState('');
  const [form, setForm] = useState({
    expense_date: new Date().toISOString().slice(0, 10),
    category_id: '',
    equipment_id: '',
    staff_id: '',
    description: '',
    amount: '',
    payment_mode: 'CASH',
    paid_to: '',
    invoice_url: '',
  });

  useEffect(() => {
    Promise.all([
      expensesAPI.getCategories(),
      staffAPI.list(),
      equipmentAPI.list(),
    ]).then(([catRes, staffRes, eqRes]) => {
      setCategories(catRes.data || []);
      setStaffList(staffRes.data || []);
      setEquipmentList(eqRes.data || []);
    }).catch(console.error);
  }, []);

  useEffect(() => {
    if (!isEdit || !id) return;
    setLoadingExpense(true);
    expensesAPI.getById(id)
      .then((res) => {
        const d = res.data;
        setForm({
          expense_date: d.expense_date ? String(d.expense_date).slice(0, 10) : '',
          category_id: d.category_id != null ? String(d.category_id) : '',
          equipment_id: d.equipment_id != null ? String(d.equipment_id) : '',
          staff_id: d.staff_id != null ? String(d.staff_id) : '',
          description: d.description || '',
          amount: d.amount != null ? String(d.amount) : '',
          payment_mode: d.payment_mode || 'CASH',
          paid_to: d.paid_to || '',
          invoice_url: d.invoice_url || '',
        });
        setReceiptPreview(d.invoice_url ? resolveUploadUrl(d.invoice_url) : '');
        receiptFileRef.current = null;
      })
      .catch(() => navigate('/dashboard/expenses'))
      .finally(() => setLoadingExpense(false));
  }, [id, isEdit, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.category_id || !form.expense_date || form.amount === '' || form.amount == null) {
      alert('Date, category and amount are required');
      return;
    }
    setLoading(true);
    try {
      let invoiceUrl = form.invoice_url || null;
      if (receiptFileRef.current) {
        const uploadRes = await expensesAPI.uploadReceipt(receiptFileRef.current);
        invoiceUrl = uploadRes?.data?.url || null;
      }
      const payload = {
        ...form,
        amount: parseFloat(form.amount) || 0,
        equipment_id: form.equipment_id || null,
        staff_id: form.staff_id || null,
        invoice_url: invoiceUrl,
      };
      if (isEdit) {
        await expensesAPI.update(id, payload);
      } else {
        await expensesAPI.create(payload);
      }
      navigate('/dashboard/expenses');
    } catch (err) {
      alert(err.message || (isEdit ? 'Failed to update expense' : 'Failed to create expense'));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = 'ga-input w-full rounded-lg';
  const labelCls = 'block text-sm font-medium text-ga-text-muted mb-1';

  if (loadingExpense) {
    return (
      <DashboardLayout>
        <div className="p-8 text-ga-text-muted">Loading...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl space-y-6">
        <div className="flex items-center gap-4">
          <Link
            to="/dashboard/expenses"
            aria-label="Back to expenses"
            className="p-2 rounded-lg text-ga-text-primary hover:bg-ga-bg-surface-subtle"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-ga-text-primary">
              {isEdit ? 'Edit expense' : 'Add expense'}
            </h1>
            <p className="text-sm text-ga-text-muted mt-0.5">
              {isEdit ? 'Update amount, category, or payment details.' : 'Record a new ground expense.'}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="ga-card rounded-ga-lg shadow-ga-soft border border-ga-border-subtle p-6 space-y-4">
          <div>
            <label className={labelCls}>Expense date *</label>
            <input
              type="date"
              value={form.expense_date}
              onChange={(e) => setForm((f) => ({ ...f, expense_date: e.target.value }))}
              className={inputCls}
              required
            />
          </div>
          <div>
            <label className={labelCls}>Category *</label>
            <select
              value={form.category_id}
              onChange={(e) => setForm((f) => ({ ...f, category_id: e.target.value }))}
              className={inputCls}
              required
            >
              <option value="">Select category</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.category_name}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Equipment (optional)</label>
              <select
                value={form.equipment_id}
                onChange={(e) => setForm((f) => ({ ...f, equipment_id: e.target.value }))}
                className={inputCls}
              >
                <option value="">None</option>
                {equipmentList.map((eq) => (
                  <option key={eq.id} value={eq.id}>{eq.equipment_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Staff (optional)</label>
              <select
                value={form.staff_id}
                onChange={(e) => setForm((f) => ({ ...f, staff_id: e.target.value }))}
                className={inputCls}
              >
                <option value="">None</option>
                {staffList.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={2}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Amount (₹) *</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.amount}
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
              className={inputCls}
              required
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Payment mode</label>
              <select
                value={form.payment_mode}
                onChange={(e) => setForm((f) => ({ ...f, payment_mode: e.target.value }))}
                className={inputCls}
              >
                <option value="CASH">Cash</option>
                <option value="UPI">UPI</option>
                <option value="BANK">Bank</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Paid to</label>
              <input
                type="text"
                value={form.paid_to}
                onChange={(e) => setForm((f) => ({ ...f, paid_to: e.target.value }))}
                className={inputCls}
              />
            </div>
          </div>

          <ImageCaptureField
            label="Receipt photo"
            valueUrl={receiptPreview}
            attachedHint="Attached — will be saved with this expense"
            onChange={({ file, url }) => {
              receiptFileRef.current = file;
              if (!file) {
                setReceiptPreview('');
                setForm((f) => ({ ...f, invoice_url: '' }));
              } else {
                setReceiptPreview(url);
              }
            }}
          />

          <div className="flex flex-wrap gap-2 pt-2 sticky bottom-0 bg-ga-bg-surface py-3 -mx-1 px-1 border-t border-ga-border-subtle sm:static sm:border-0 sm:py-0">
            <button type="submit" disabled={loading} className="ga-btn-primary inline-flex items-center gap-2 px-4 py-2 rounded-lg">
              <Save className="h-4 w-4" />
              {loading ? 'Saving…' : (isEdit ? 'Save changes' : 'Create expense')}
            </button>
            <button type="button" onClick={() => navigate('/dashboard/expenses')} className="ga-btn-ghost px-4 py-2 rounded-lg">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
